import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { DataTable, type Column, type FilterOption } from './DataTable';
import {
  Button,
  ConfirmDialog,
  Field,
  Input,
  Modal,
  PageHeader,
  Select,
  Textarea,
  Toast,
} from './ui';

export type FieldType = 'text' | 'email' | 'password' | 'number' | 'select' | 'checkbox' | 'json';

export interface FormField {
  name: string;
  label: string;
  type?: FieldType;
  required?: boolean;
  hint?: string;
  placeholder?: string;
  options?: Array<{ value: string; label: string }>;
  /** Shown on create only (e.g. an immutable code), or update only. */
  only?: 'create' | 'update';
  /**
   * Set once, then fixed — a record's identity (a Vicidial username, a SIP
   * username, a bot code). Still shown when editing, so the operator can see
   * which record this is, but disabled and never sent in the PATCH.
   *
   * This is not cosmetic. The update models reject these outright, so sending
   * one back made every edit fail with "Extra inputs are not permitted" —
   * on six different pages.
   */
  immutable?: boolean;
  defaultValue?: string | number | boolean;
}

interface CrudPageProps<T> {
  title: string;
  description?: string;
  resource: string;
  path: string;
  queryKey: string;
  columns: Array<Column<T>>;
  fields: FormField[];
  filters?: FilterOption[];
  searchPlaceholder?: string;
  emptyTitle?: string;
  emptyDescription?: string;
  onRowClick?: (row: T) => void;
  extraActions?: ReactNode;
  /** Rendered above the table — callouts, help, related widgets. */
  children?: ReactNode;
  /** Reported as the row count changes, so a page can show it. */
  onTotal?: (total: number) => void;
  /**
   * Offer a delete on each row.
   *
   * Opt-in rather than on everywhere. The generic DELETE has existed on every
   * resource since these pages were generated and no page ever called it, so
   * a bot could be created and never removed. Turning it on for all of them
   * at once would also put an irreversible button on the tables where it is
   * genuinely irreversible; each page should decide.
   */
  deletable?: boolean;
  /**
   * What the row's own name is, for the confirmation. Falls back to `name`.
   */
  describeRow?: (row: T) => string;
  /**
   * What actually happens, in the operator's words. Soft-deleting resources
   * say "archived", the rest say "deleted" — the dialog must not promise the
   * wrong one.
   */
  deleteExplanation?: ReactNode;
  /**
   * Anything else the delete dialog should offer for this row.
   *
   * Exists so a page whose delete is a SOFT one can put the permanent
   * version in the same place -- behind its own confirmation, in the dialog
   * that has just explained the difference. Two buttons on the row would ask
   * somebody to know the difference before reading it.
   */
  deleteExtra?: (row: T) => ReactNode;
  createLabel?: string;
}

type FormValues = Record<string, string | number | boolean>;

function initialValues(fields: FormField[], mode: 'create' | 'update'): FormValues {
  const values: FormValues = {};
  for (const field of fields) {
    if (field.only && field.only !== mode) continue;
    values[field.name] =
      field.defaultValue ?? (field.type === 'checkbox' ? false : '');
  }
  return values;
}

/** Prefill the edit form from the row being edited. */
function valuesFromRow(fields: FormField[], row: Record<string, unknown>): FormValues {
  const values: FormValues = {};
  for (const field of fields) {
    if (field.only === 'create') continue;
    const current = row[field.name];

    if (field.type === 'checkbox') {
      values[field.name] = Boolean(current);
    } else if (field.type === 'json') {
      values[field.name] =
        current === null || current === undefined ? '' : JSON.stringify(current, null, 2);
    } else if (field.type === 'password') {
      // Never prefilled: the API does not return secrets, and a masked
      // placeholder would be saved back as the literal password.
      values[field.name] = '';
    } else {
      values[field.name] = current === null || current === undefined ? '' : String(current);
    }
  }
  return values;
}

/** Strip blanks so PATCH sends only what the operator actually changed. */
function toPayload(fields: FormField[], values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const field of fields) {
    const raw = values[field.name];
    // Only blanks are skipped. `false` is a value and must survive, or
    // unticking a checkbox would silently send nothing.
    if (raw === '' || raw === undefined) continue;
    if (field.type === 'number') {
      payload[field.name] = Number(raw);
    } else if (field.type === 'json') {
      try {
        payload[field.name] = JSON.parse(String(raw));
      } catch {
        throw new Error(`${field.label} must be valid JSON`);
      }
    } else {
      payload[field.name] = raw;
    }
  }
  return payload;
}

export function CrudPage<T extends { id?: string }>({
  title,
  description,
  resource,
  path,
  queryKey,
  columns,
  fields,
  filters,
  searchPlaceholder,
  emptyTitle,
  emptyDescription,
  onRowClick,
  extraActions,
  children,
  onTotal,
  deletable,
  describeRow,
  deleteExplanation,
  deleteExtra,
  createLabel,
}: CrudPageProps<T>) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<T | null>(null);
  const [deleting, setDeleting] = useState<T | null>(null);
  const [values, setValues] = useState<FormValues>(() => initialValues(fields, 'create'));
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ message: string; tone: 'good' | 'bad' } | null>(null);

  const create = useMutation({
    mutationFn: (payload: Record<string, unknown>) => api.post(path, payload),
    onSuccess: () => {
      setOpen(false);
      setValues(initialValues(fields, 'create'));
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setToast({ message: `${title.replace(/s$/, '')} created`, tone: 'good' });
    },
    onError: (err) =>
      setFormError(errorMessage(err, 'Could not save')),
  });

  const update = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      api.patch(`${path}/${id}`, payload),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setToast({ message: `${title.replace(/s$/, '')} updated`, tone: 'good' });
    },
    onError: (err) => setFormError(errorMessage(err, 'Could not save')),
  });

  const remove = useMutation({
    mutationFn: (row: T) =>
      api.delete(`${path}/${(row as unknown as { id: string }).id}`),
    onSuccess: () => {
      setDeleting(null);
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setToast({ message: `${title.replace(/s$/, '')} removed`, tone: 'good' });
    },
    onError: (err) => {
      setDeleting(null);
      setToast({ message: errorMessage(err, 'Could not remove it'), tone: 'bad' });
    },
  });

  const startEdit = (row: T) => {
    setFormError(null);
    setValues(valuesFromRow(fields, row as unknown as Record<string, unknown>));
    setEditing(row);
  };

  const submitEdit = () => {
    setFormError(null);
    if (!editing?.id) return;
    try {
      update.mutate({
        id: editing.id,
        payload: toPayload(
          fields.filter((f) => f.only !== 'create' && !f.immutable),
          values,
        ),
      });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Invalid input');
    }
  };

  const submit = () => {
    setFormError(null);
    const missing = fields.find(
      (f) => f.required && f.only !== 'update' && !values[f.name],
    );
    if (missing) {
      setFormError(`${missing.label} is required`);
      return;
    }
    try {
      create.mutate(toPayload(fields.filter((f) => f.only !== 'update'), values));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Invalid input');
    }
  };

  const canCreate = can(`${resource}:create`);
  const canUpdate = can(`${resource}:update`);
  const canDelete = deletable === true && can(`${resource}:delete`);

  return (
    <div className="mx-auto max-w-7xl">
      <PageHeader
        title={title}
        description={description}
        actions={
          <>
            {extraActions}
            {canCreate && (
              <Button onClick={() => setOpen(true)}>
                {createLabel ?? `New ${title.replace(/s$/, '').toLowerCase()}`}
              </Button>
            )}
          </>
        }
      />

      {children}

      <DataTable<T>
        queryKey={queryKey}
        path={path}
        onTotal={onTotal}
        columns={
          canDelete
            ? [
                ...columns,
                {
                  key: '__remove',
                  header: '',
                  render: (row: T) => (
                    // Stops the row click, which opens the editor. Somebody
                    // reaching for delete is not asking to edit it first.
                    <div onClick={(event) => event.stopPropagation()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleting(row)}
                      >
                        Delete
                      </Button>
                    </div>
                  ),
                },
              ]
            : columns
        }
        filters={filters}
        searchPlaceholder={searchPlaceholder}
        // A page that supplies `onRowClick` owns the click (usually to open a
        // detail view). Otherwise the row opens the editor — without this there
        // was no way to change an existing record at all.
        onRowClick={onRowClick ?? (canUpdate ? startEdit : undefined)}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        emptyAction={
          canCreate ? <Button onClick={() => setOpen(true)}>Create one</Button> : undefined
        }
      />

      <ConfirmDialog
        open={!!deleting}
        title={`Delete ${
          deleting
            ? describeRow?.(deleting) ??
              (deleting as unknown as { name?: string }).name ??
              'this'
            : ''
        }?`}
        confirmLabel="Delete"
        busy={remove.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && remove.mutate(deleting)}
        body={
          <div className="space-y-2 text-sm">
            {deleting && deleteExtra ? deleteExtra(deleting) : null}
            {deleteExplanation ?? (
              <p>This cannot be undone from here.</p>
            )}
          </div>
        }
      />

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={createLabel ?? `New ${title.replace(/s$/, '').toLowerCase()}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button loading={create.isPending} onClick={submit}>
              Create
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-inset ring-red-200 dark:bg-red-950/50 dark:text-red-300 dark:ring-red-900">
              {formError}
            </div>
          )}
          {fields
            .filter((field) => field.only !== 'update')
            .map((field) => (
              <FormControl
                key={field.name}
                field={field}
                value={values[field.name]}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, [field.name]: next }))
                }
              />
            ))}
        </div>
      </Modal>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={`Edit ${title.replace(/s$/, '').toLowerCase()}`}
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button loading={update.isPending} onClick={submitEdit}>
              Save changes
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300">
              {formError}
            </div>
          )}
          {fields
            .filter((field) => field.only !== 'create')
            .map((field) => (
              <FormControl
                key={field.name}
                field={
                  field.type === 'password'
                    ? { ...field, hint: field.hint ?? 'Leave blank to keep the current value.' }
                    : field
                }
                value={values[field.name]}
                disabled={field.immutable}
                onChange={(next) =>
                  setValues((prev) => ({ ...prev, [field.name]: next }))
                }
              />
            ))}
        </div>
      </Modal>

      {toast && (
        <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />
      )}
    </div>
  );
}

export function FormControl({
  field,
  value,
  onChange,
  disabled = false,
}: {
  field: FormField;
  value: string | number | boolean | undefined;
  onChange: (next: string | number | boolean) => void;
  disabled?: boolean;
}) {
  if (field.type === 'checkbox') {
    return (
      <label className="flex items-start gap-2 text-sm text-ink-700 dark:text-ink-200">
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 rounded border-ink-300 disabled:opacity-60"
        />
        <span>
          {field.label}
          {field.hint && (
            <span className="block text-xs text-ink-500 dark:text-ink-400">{field.hint}</span>
          )}
        </span>
      </label>
    );
  }

  return (
    <Field
      label={field.label}
      hint={disabled ? (field.hint ? `${field.hint} Cannot be changed after creation.` : 'Cannot be changed after creation.') : field.hint}
      required={disabled ? false : field.required}
    >
      {field.type === 'select' ? (
        <Select
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">Select…</option>
          {(field.options ?? []).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      ) : field.type === 'json' ? (
        <Textarea
          rows={6}
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder ?? '{ }'}
        />
      ) : (
        <Input
          type={field.type ?? 'text'}
          value={String(value ?? '')}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          autoComplete={field.type === 'password' ? 'new-password' : undefined}
        />
      )}
    </Field>
  );
}
